import React from 'react';
import { Check, Palette, Type, Layout } from 'lucide-react';
import { FONT_OPTIONS, COLOR_SCHEMES, LAYOUT_OPTIONS } from '../data/themeOptions';

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="w-12 h-12 bg-[#E6D7B3] rounded-lg flex items-center justify-center">
      <Icon className="h-6 w-6 text-[#001B2A]" />
    </div>
    <div>
      <h3 className="text-xl font-serif font-bold text-[#001B2A]">{title}</h3>
      <p className="text-sm text-gray-600">{subtitle}</p>
    </div>
  </div>
);

const optionButtonCls = (isSelected) =>
  `w-full text-left p-4 rounded-lg border-2 transition-all ${
    isSelected ? 'border-[#7B1E2B] bg-[#7B1E2B]/5' : 'border-gray-200 hover:border-gray-300'
  }`;

const OptionTitle = ({ name, isSelected }) => (
  <div className="flex items-center gap-2 mb-1">
    <p className="font-semibold text-[#001B2A]">{name}</p>
    {isSelected && <Check className="h-5 w-5 text-[#7B1E2B]" />}
  </div>
);

export const FontOptions = ({ selected, onSelect }) => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <SectionHeader icon={Type} title="Fonts" subtitle="Choose your typography style" />
    <div className="space-y-3">
      {FONT_OPTIONS.map((font) => (
        <button key={font.id} onClick={() => onSelect(font.id)} className={optionButtonCls(selected === font.id)}>
          <OptionTitle name={font.name} isSelected={selected === font.id} />
          <p className="text-sm text-gray-600 mb-2">{font.description}</p>
          <p className="text-lg" style={{ fontFamily: font.headingFont }}>{font.preview}</p>
        </button>
      ))}
    </div>
  </div>
);

export const ColorOptions = ({ selected, onSelect }) => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <SectionHeader icon={Palette} title="Colors" subtitle="Select your color palette" />
    <div className="space-y-3">
      {COLOR_SCHEMES.map((scheme) => (
        <button key={scheme.id} onClick={() => onSelect(scheme.id)} className={optionButtonCls(selected === scheme.id)}>
          <OptionTitle name={scheme.name} isSelected={selected === scheme.id} />
          <p className="text-sm text-gray-600 mb-3">{scheme.description}</p>
          <div className="flex gap-2">
            {Object.entries(scheme.colors).slice(0, 5).map(([name, color]) => (
              <div key={name} className="w-10 h-10 rounded-md border border-gray-300" style={{ backgroundColor: color }} title={name} />
            ))}
          </div>
        </button>
      ))}
    </div>
  </div>
);

export const LayoutOptions = ({ selected, onSelect }) => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <SectionHeader icon={Layout} title="Layout" subtitle="Choose your page structure" />
    <div className="space-y-3">
      {LAYOUT_OPTIONS.map((layout) => (
        <button key={layout.id} onClick={() => onSelect(layout.id)} className={optionButtonCls(selected === layout.id)}>
          <OptionTitle name={layout.name} isSelected={selected === layout.id} />
          <p className="text-sm text-gray-600 mb-2">{layout.description}</p>
          <p className="text-xs text-gray-500 italic">{layout.preview}</p>
        </button>
      ))}
    </div>
  </div>
);

export const LivePreview = ({ font, color }) => (
  <div className="mt-8 bg-white rounded-lg shadow-md p-6">
    <h3 className="text-xl font-serif font-bold text-[#001B2A] mb-4">Live Preview</h3>
    <div
      className="border-2 border-gray-200 rounded-lg p-8"
      style={{ backgroundColor: color.colors.light, fontFamily: font.bodyFont }}
    >
      <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: font.headingFont, color: color.colors.primary }}>
        The Bow Tie Guy
      </h1>
      <p className="text-lg mb-6" style={{ color: color.colors.neutral }}>
        Premium bow ties and menswear for the distinguished gentleman.
      </p>
      <div className="flex gap-4">
        <button
          className="px-6 py-3 rounded-md font-semibold transition-all"
          style={{ backgroundColor: color.colors.primary, color: color.colors.light }}
        >
          Shop Bow Ties
        </button>
        <button
          className="px-6 py-3 rounded-md font-semibold border-2 transition-all"
          style={{ borderColor: color.colors.secondary, color: color.colors.secondary }}
        >
          View Collection
        </button>
      </div>
    </div>
  </div>
);
